import { useCallback, useEffect, useState } from 'react';
import { useApiFetch } from './useApiFetch';

export interface SampleMeta {
  location: string;
  filename: string;
  eval_set_id: string;
  epoch: number;
  id: string;
  uuid: string;
}

export const useSampleMeta = (sampleUuid?: string) => {
  const [sampleMeta, setSampleMeta] = useState<SampleMeta | null>(null);
  const { apiFetch, isLoading, error } = useApiFetch();

  const getSampleMeta = useCallback(
    async (uuid: string) => {
      const sampleMetaUrl = `/meta/samples/${encodeURIComponent(uuid)}`;
      const response = await apiFetch(sampleMetaUrl);
      if (!response) {
        throw new Error('Failed to fetch sample metadata');
      }
      const data = (await response.json()) as SampleMeta;
      return data;
    },
    [apiFetch]
  );

  useEffect(() => {
    if (!sampleUuid) return;

    let cancelled = false;

    const fetchSampleMeta = async () => {
      try {
        const data = await getSampleMeta(sampleUuid);
        if (!cancelled) setSampleMeta(data);
      } catch {
        // A sample that isn't in the warehouse yet 404s, which `apiFetch`
        // surfaces as a rejection. Callers read the failure off the hook's
        // `error`, so swallow it rather than let it escape the effect
        // unhandled.
      }
    };

    void fetchSampleMeta();

    return () => {
      cancelled = true;
    };
  }, [sampleUuid, getSampleMeta]);

  return { sampleMeta, isLoading, error };
};
